import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CurrencyService {
  private readonly myServyUrl = environment.apimyservy;

  constructor(private http: HttpClient) {}

  /** Headers for the main API (ts-sit). Auth added by interceptor. */
  private buildAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  /** Normalize a raw currency object to { id, isoCode, name } */
  private normalizeCurrency(raw: any): any {
    if (!raw) return raw;
    let iso = raw?.isoCode || raw?.IsoCode || raw?.code || raw?.Code || '';
    if (iso === '4217' || raw?.name === 'USD') iso = 'USD';
    return {
      id: raw?.id || raw?.Id || raw?.currencyId || raw?.CurrencyId || '',
      isoCode: iso || raw?.name || '',
      name: raw?.name || raw?.Name || iso || '',
    };
  }

  getCurrencies(): Observable<any[]> {
    const url = `${this.myServyUrl}/api/v1/currencies/filter-paginated`;
    const body = { page: 1, pageSize: 200 };
    console.log('[CurrencyService] fetching currencies from mc-sit directly');
    return this.http.post<any>(url, body, { headers: this.buildAuthHeaders() }).pipe(
      map((res) => {
        const list = Array.isArray(res?.result) ? res.result : (res?.list || res?.data || res?.items || res || []);
        const normalizedList = Array.isArray(list) ? list.map(this.normalizeCurrency) : [];
        console.log('[CurrencyService] Fetched and normalized currencies list:', normalizedList);
        return normalizedList;
      })
    );
  }

  /**
   * Current exchange rate (with optional first-day rate usage)
   * @param sci ServiceCenterId
   * @param currencyid CurrencyId
   * @param firstDayRate boolean or flag controlling first-day rate usage
   */
  getTasa(sci: any, currencyid: any, firstDayRate?: any): Observable<any> {
    // For development, use the hardcoded exchange rate endpoint and dev values requested:
    const devCurrencyId = '019ef5ab-40c3-71d6-9085-55251c305d70';
    const devWorkspaceId = '019faf93-1f75-740f-bff7-d5234b0c26b7';
    
    const path = `${this.myServyUrl}/api/v1/exchange-rates/${devCurrencyId}/current?workspaceId=${devWorkspaceId}`;
    return this.http.get(path, { headers: this.buildAuthHeaders() });
  }


  /**
   * Retrieve a single currency by id
   */
  getCurrency(currencyid: string): Observable<any> {
    // Defensive: avoid calling backend with invalid ids like undefined/null/empty
    if (!currencyid || currencyid === 'undefined' || currencyid === 'null') {
      console.warn(
        '[CurrencyService] getCurrency called with invalid id:',
        currencyid
      );
      return of(null);
    }
    if (currencyid === 'usd-id') {
      return of({ id: 'usd-id', isoCode: 'USD', name: 'USD', symbol: '$' });
    }
    if (
      currencyid === 'cfb82af5-8249-4a23-b3fd-b323af04bfe5' ||
      currencyid === 'aacec7b6-e44d-4136-9827-149d562b9bae' ||
      currencyid === '019ef5ab-40c3-71d6-9085-55251c305d70' ||
      currencyid === environment.vesId
    ) {
      return of({
        id: currencyid,
        isoCode: 'VES',
        name: 'VES',
        symbol: 'Bs.'
      });
    }
    const path = `${this.myServyUrl}/api/v1/currencies/${currencyid}`;
    return this.http.get(path, { headers: this.buildAuthHeaders() }).pipe(
      map((res: any) => {
        const raw = res?.result || res?.entity || res?.data || res?.response || res;
        return this.normalizeCurrency(raw);
      })
    );
  }
}
